import React, { useState } from 'react';
import { Loader2, Check, Stethoscope, TrendingUp, Star, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { METROS } from '../mock';
import api from '../api';

const BENEFITS = [
  { icon: TrendingUp, title: 'Local leads', desc: 'Get in front of nearby cat & dog owners searching for care.' },
  { icon: Star, title: 'Featured listing', desc: 'Stand out in Purrflect + Wooflect vet finder results.' },
  { icon: Phone, title: 'Direct inbound', desc: 'One-tap Call & Directions drive real appointments.' },
];

export default function VetJoin() {
  const [f, setF] = useState({ clinic: '', contact_name: '', email: '', phone: '', city: METROS[0], plan_interest: 'basic', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.clinic || !f.email) { toast.error('Clinic name and email are required'); return; }
    setBusy(true);
    try {
      await api.post('/vets/lead', f);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10">
      <div className="mx-auto max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold text-[hsl(var(--coral))]">
          <Stethoscope className="h-3.5 w-3.5" /> Founding Vet Partner
        </div>
        <h1 className="mt-4 text-4xl font-extrabold leading-tight sm:text-5xl">List your clinic on Purrflect &amp; Wooflect.</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">Founding partners get <b>60 days free</b> and a locked founder rate. Basic listing $79/mo, Premium $199/mo — across both the cat and dog apps.</p>

        <div className="mt-7 grid gap-3 sm:grid-cols-3">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl bg-white p-4 card-soft">
              <b.icon className="h-5 w-5 text-[hsl(var(--coral))]" />
              <p className="mt-2 font-bold">{b.title}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>

        {!done ? (
          <form onSubmit={submit} className="mt-8 rounded-3xl bg-white p-6 card-soft" data-testid="vet-lead-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="text-sm font-semibold">Clinic name</Label><Input value={f.clinic} onChange={set('clinic')} data-testid="vet-clinic" placeholder="Rose City Cat Clinic" className="mt-1.5 h-12 rounded-xl bg-white" /></div>
              <div><Label className="text-sm font-semibold">Your name</Label><Input value={f.contact_name} onChange={set('contact_name')} placeholder="Dr. Jane Doe" className="mt-1.5 h-12 rounded-xl bg-white" /></div>
              <div><Label className="text-sm font-semibold">Email</Label><Input type="email" value={f.email} onChange={set('email')} data-testid="vet-email" placeholder="clinic@example.com" className="mt-1.5 h-12 rounded-xl bg-white" /></div>
              <div><Label className="text-sm font-semibold">Phone</Label><Input value={f.phone} onChange={set('phone')} placeholder="+1 503 555 0100" className="mt-1.5 h-12 rounded-xl bg-white" /></div>
              <div>
                <Label className="text-sm font-semibold">City</Label>
                <select value={f.city} onChange={set('city')} data-testid="vet-city" className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                  {METROS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Plan interest</Label>
                <select value={f.plan_interest} onChange={set('plan_interest')} className="mt-1.5 h-12 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                  <option value="basic">Basic — $79/mo (both apps)</option>
                  <option value="premium">Premium — $199/mo (both apps)</option>
                </select>
              </div>
            </div>
            <div className="mt-4"><Label className="text-sm font-semibold">Anything else? <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <textarea value={f.message} onChange={set('message')} rows={3} placeholder="Hours, services, questions…" className="mt-1.5 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
            <button type="submit" disabled={busy} data-testid="vet-submit" className="coral-btn mt-5 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />}Request my founding listing
            </button>
          </form>
        ) : (
          <div data-testid="vet-success" className="mt-8 rounded-3xl bg-white p-8 text-center card-soft">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent"><Check className="h-7 w-7 text-[hsl(var(--coral))]" /></div>
            <h2 className="mt-4 text-2xl font-extrabold">Thanks — we'll be in touch!</h2>
            <p className="mt-2 text-muted-foreground">Our team will reach out within 1–2 business days to set up your founding listing.</p>
          </div>
        )}
      </div>
    </div>
  );
}
